import { ChevronDownIcon } from '@/icons';
import {
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
} from 'antd';
import dayjs from 'dayjs';
import { useGetAllOfficesQuery } from '../../../../../../app/features/control-panel/organization/organization.api';
import { useGetAllOvertimePoliciesQuery } from '../../../../../../app/features/control-panel/policies/policies.api';
import { useGetAllEmployeesQuery } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import ModuleGuard from '../../../../../../components/Guards/ModuleGuard';
import MODULES from '../../../../../../config/modules.config';
import useAuth from '../../../../../../hooks/useAuth';
import useError from '../../../../../../hooks/useError';
import { selectArrayOption } from '../../../../../../utils/global.utils';
import PayTypeGradeSalaryFields from '../shared/PayTypeGradeSalaryFields';
type JobInformationProps = {
  departmentsData: any;
  designationsData: any;
};

export default function JobInformation({
  departmentsData,
  designationsData,
}: JobInformationProps) {
  const form = Form.useFormInstance();
  const officeId = Form.useWatch('office_id', form);
  const { profileInfo } = useAuth();
  const {
    data: employeesData,
    isLoading: employeesLoading,
    error: employeeError,
  } = useGetAllEmployeesQuery({
    params: {
      page: 0,
      limit: 0,
    },
  });
  // Get all overtime policies
  const { data: overtimePoliciesData } = useGetAllOvertimePoliciesQuery(
    { params: { skip: 0, take: 0, office_id: officeId } },
    { skip: !officeId },
  );

  // Get all offices for parent office selection
  const { data: allOfficesData } = useGetAllOfficesQuery(
    { params: { skip: 0, take: 0, status: 'ACTIVE' } },
    { skip: false },
  );

  useError(employeeError, 'Failed to get employee');

  // Use API data directly
  const employees = employeesData?.data?.data || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Join Date */}
      <Form.Item
        label={'Join Date'}
        name="joining_date"
        rules={[
          {
            required: true,
            message: 'Joining date is required',
          },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const oneYearFromNow = dayjs().add(1, 'year');
              if (dayjs(value).isAfter(oneYearFromNow)) {
                return Promise.reject(
                  new Error(
                    'The joining date should not be more than one year in the future.',
                  ),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <DatePicker placeholder="dd/mm/yyyy" showToday={false} />
      </Form.Item>

      {/* Resignation Date: optional, any employee; feeds payroll pro-rata */}
      <Form.Item
        label="Resignation Date"
        name="resign_date"
        dependencies={['joining_date']}
        rules={[
          ({ getFieldValue }) => ({
            validator: (_, value) => {
              const joined = getFieldValue('joining_date');
              if (!value || !joined || !dayjs(value).isBefore(joined, 'day')) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error('Resignation date cannot be before the joining date.'),
              );
            },
          }),
        ]}
      >
        <DatePicker
          size="large"
          className="w-full"
          placeholder="dd/mm/yyyy"
          showToday={false}
        />
      </Form.Item>

      {/* Emp Type */}
      <Form.Item
        label="Employee Type"
        name="employee_type"
        rules={[{ required: true, message: 'Please select type' }]}
      >
        <Select
          size="large"
          className="w-full"
          placeholder="Select Type"
          options={[
            { value: 'full-time', label: 'Full Time' },
            { value: 'part-time', label: 'Part Time' },
            { value: 'contract', label: 'Contract' },
          ]}
        />
      </Form.Item>

      {/* Assigned To */}
      <Form.Item
        label="Assigned To"
        name="manager_id"
        rules={[{ required: true, message: 'Please select manager' }]}
      >
        <Select
          size="large"
          className="w-full"
          placeholder="Select Manager"
          allowClear
          showSearch
          optionFilterProp="label"
          loading={employeesLoading}
          options={employees?.map((emp) => ({
            label: emp?.name,
            value: emp?.user_id,
          }))}
        />
      </Form.Item>

      {/* Employee ID */}
      <Form.Item
        label="Employee ID"
        name="employee_id"
        rules={[
          { required: true, message: 'Please enter employee ID' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve(); // empty handled by required

              const firstChar = value.charAt(0);

              // Leading space
              if (/\s/.test(value)) {
                return Promise.reject('Employee ID should not contain spaces.');
              }

              // Negative number check (if Employee ID is numeric)
              if (!isNaN(Number(value)) && Number(value) < 0) {
                return Promise.reject('Employee ID cannot be negative.');
              }

              // Decimal check
              if (!isNaN(Number(value)) && value.includes('.')) {
                return Promise.reject(
                  'Employee ID cannot be a decimal number.',
                );
              }

              // Starting special character
              if (/[^a-zA-Z0-9]/.test(firstChar)) {
                return Promise.reject(
                  'Employee ID should not start with a special character.',
                );
              }

              // Any space inside
              if (/\s/.test(value)) {
                return Promise.reject(
                  'Employee ID leading and trailing spaces should not be allowed.',
                );
              }

              // ✅ Only allow letters, numbers, hyphen (-), underscore (_)
              const validPattern = /^[a-zA-Z0-9_-]+$/;

              if (!validPattern.test(value)) {
                return Promise.reject(
                  'Employee ID accepts letters, numbers, - and _ only.',
                );
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input size="large" placeholder="Employee ID" />
      </Form.Item>

      {/* Employee Office */}
      <ModuleGuard moduleId={MODULES.CONTROL_PANEL_ORGANIZATION}>
        <Form.Item
          label="Select Branch"
          name="office_id"
          rules={[{ required: true, message: 'Please branch' }]}
          className="col-span-2"
        >
          <Select
            showSearch
            allowClear
            placeholder="Select"
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            optionFilterProp="label"
            options={selectArrayOption(
              allOfficesData?.data?.data || [],
              'name',
              'id',
            )}
          />
        </Form.Item>
      </ModuleGuard>

      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-6">
        <Form.Item
          label="Geo Fencing"
          name="is_geo_enable"
          layout="horizontal"
          className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
          valuePropName="checked"
        >
          <Switch className="custom-switch" />
        </Form.Item>
        <Form.Item
          label="Flexi Hour"
          name="flexi_hour"
          layout="horizontal"
          className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
          valuePropName="checked"
        >
          <Switch disabled className="custom-switch" />
        </Form.Item>
        {/* Facial Recognition */}
        <Form.Item
          label="Facial Recognition"
          name="is_face_recognition_enable"
          layout="horizontal"
          className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
          valuePropName="checked"
        >
          <Switch
            disabled={
              !profileInfo?.organization?.meta_hrx?.is_face_recognition_enable
            }
            className="custom-switch"
          />
        </Form.Item>

        <ModuleGuard moduleId={MODULES.EMPLOYEE_POLICIES} fallback={<div />}>
          <Form.Item
            label="Over Time "
            name="is_overtime_enable"
            layout="horizontal"
            className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
            valuePropName="checked"
          >
            <Switch className="custom-switch" />
          </Form.Item>
        </ModuleGuard>
      </div>
      {/* Fence Attendance Permission  */}
      <Form.Item
        label="Fence Attendance Permission"
        name="fence_attendance_permission"
        rules={[{ required: true, message: 'Please select permission' }]}
      >
        <Select
          size="large"
          className="w-full"
          placeholder="Select"
          options={[
            { value: 'BOTH', label: 'Both' },
            { value: 'CHECK_IN_ONLY', label: 'Only Check In' },
            { value: 'CHECK_OUT_ONLY', label: 'Only Check Out' },
          ]}
        />
      </Form.Item>

      {/* Over Time Policy - Conditional based on Feature Module Lock */}
      <ModuleGuard moduleId={MODULES.EMPLOYEE_POLICIES}>
        <Form.Item label="Select Over Time Policy" name="overtime_policy_id">
          <Select
            size="large"
            className="w-full"
            placeholder="Select"
            disabled={!officeId}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            optionFilterProp="label"
            options={selectArrayOption(
              overtimePoliciesData?.data?.data || [],
              'policy_name',
              'id',
            )}
          />
        </Form.Item>
      </ModuleGuard>

      {/* Department */}
      <Form.Item
        label="Department"
        name="department_id"
        rules={[{ required: true, message: 'Please select department' }]}
      >
        <Select
          size="large"
          className="w-full"
          placeholder="Select"
          options={departmentsData?.data?.data?.map((dept: any) => ({
            label: dept.name,
            value: dept.id,
          }))}
        />
      </Form.Item>

      {/* Designation */}
      <Form.Item
        label="Designation"
        name="designation_id"
        rules={[{ required: true, message: 'Please select designation' }]}
      >
        <Select
          size="large"
          className="w-full"
          placeholder="Select"
          options={designationsData?.data?.data?.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
        />
      </Form.Item>

      <div className="col-span-2 grid grid-cols-2 gap-4">
        <PayTypeGradeSalaryFields />
      </div>
    </div>
  );
}
